/**
 * 세션 전체 결제 i18n 라벨 빌더 (#603 [3][4] 공유 → #661 전용 페이지 전환).
 *
 * SessionChargePage(#661 전용 페이지)가 헤더(title/description)와 SessionChargePanel
 * (보정치/카드 폼/에러) 라벨을 단일 빌더로 조립한다 — 중복 제거를 위해 추출했다.
 *
 * - title/description/session/adjustments/errors: payNowTranslations (15언어 전수) — locale 인덱싱.
 * - cardInput: Bloomin 카드 폼 라벨 (#522 — 15언어 전수 정의, locale 직접 인덱싱).
 *
 * @see src/components/payment/SessionChargePanel.tsx
 * @see src/pages/SessionChargePage.tsx
 * @see src/i18n/paymentModal.ts payNowTranslations / bloominPaymentTranslations
 */
import type { Locale } from '@/config/locales';
import type { SessionChargePanelProps } from './SessionChargePanel';
import { bloominPaymentTranslations, payNowTranslations } from '@/i18n/paymentModal';

const paymentMethodResolutionErrorTranslations: Record<Locale, string> = {
  es: 'No pudimos verificar los métodos de pago disponibles. Inténtalo de nuevo.',
  en: 'We could not verify the available payment methods. Please try again.',
  ko: '사용 가능한 결제 수단을 확인하지 못했습니다. 다시 시도해 주세요.',
  pt: 'Não foi possível verificar os métodos de pagamento disponíveis. Tente novamente.',
  fr: 'Nous n’avons pas pu vérifier les modes de paiement disponibles. Veuillez réessayer.',
  zh: '无法确认可用的付款方式。请重试。',
  ja: '利用可能な支払い方法を確認できませんでした。もう一度お試しください。',
  hi: 'उपलब्ध भुगतान विधियों की पुष्टि नहीं हो सकी। कृपया फिर से प्रयास करें।',
  de: 'Die verfügbaren Zahlungsmethoden konnten nicht geprüft werden. Bitte versuche es erneut.',
  it: 'Non è stato possibile verificare i metodi di pagamento disponibili. Riprova.',
  ru: 'Не удалось проверить доступные способы оплаты. Попробуйте ещё раз.',
  ar: 'تعذر التحقق من طرق الدفع المتاحة. يرجى المحاولة مرة أخرى.',
  vi: 'Không thể xác minh các phương thức thanh toán hiện có. Vui lòng thử lại.',
  th: 'ไม่สามารถตรวจสอบวิธีการชำระเงินที่ใช้ได้ โปรดลองอีกครั้ง',
  nl: 'We konden de beschikbare betaalmethoden niet controleren. Probeer het opnieuw.',
};

const manualPostpayNoticeTranslations: Record<
  Locale,
  NonNullable<SessionChargePanelProps['labels']['manualPostpayNotice']>
> = {
  es: {
    title: 'Pago en caja',
    body: 'Esta mesa está configurada para pago en efectivo en tienda. Pide al personal que confirme el cobro.',
    adjustmentsPolicy: 'Las propinas se manejan directamente con el personal y los puntos no están disponibles para este método de pago en tienda.',
    requestAmountLabel: 'Monto solicitado en caja (sin propina ni puntos)',
    requestButton: 'Solicitar pago en efectivo',
    requested: 'Solicitud enviada al POS',
    posCardReaderTitle: 'Pago con terminal',
    posCardReaderBody: 'Esta mesa está configurada para pago con terminal de tarjeta en tienda. Pide al personal que lleve la terminal.',
    posCardReaderRequestButton: 'Solicitar terminal de tarjeta',
    posCardReaderRequested: 'Solicitud enviada al POS',
  },
  en: {
    title: 'Pay at the store',
    body: 'This table is configured for in-store cash postpay. Ask the staff to confirm the payment.',
    adjustmentsPolicy: 'Tips are handled directly with the staff, and points are not available for this in-store payment method.',
    requestAmountLabel: 'In-store request amount (excluding tip and points)',
    requestButton: 'Request cash payment',
    requested: 'Request sent to POS',
    posCardReaderTitle: 'Pay with card reader',
    posCardReaderBody: 'This table is configured for in-store card reader postpay. Ask the staff to bring the terminal.',
    posCardReaderRequestButton: 'Request card reader',
    posCardReaderRequested: 'Request sent to POS',
  },
  ko: {
    title: '매장 현금 결제',
    body: '이 테이블은 매장 현금 후불로 설정되어 있습니다. 직원에게 결제 확인을 요청해 주세요.',
    adjustmentsPolicy: '팁은 직원과 현장에서 직접 처리하며, 이 매장 결제 방식에서는 포인트를 사용할 수 없습니다.',
    requestAmountLabel: '현장 결제 요청 금액(팁·포인트 제외)',
    requestButton: '현금 결제 요청',
    requested: '포스에 요청을 보냈습니다',
    posCardReaderTitle: '매장 카드리더기 결제',
    posCardReaderBody: '이 테이블은 매장 카드리더기 후불로 설정되어 있습니다. 직원에게 카드 단말기를 요청해 주세요.',
    posCardReaderRequestButton: '카드리더기 요청',
    posCardReaderRequested: '포스에 요청을 보냈습니다',
  },
  pt: {
    title: 'Pagamento no caixa',
    body: 'Esta mesa está configurada para pós-pagamento em dinheiro na loja. Peça à equipe para confirmar o pagamento.',
    adjustmentsPolicy: 'As gorjetas são tratadas diretamente com a equipe e os pontos não estão disponíveis para este método de pagamento na loja.',
    requestAmountLabel: 'Valor solicitado no caixa (sem gorjeta nem pontos)',
    requestButton: 'Solicitar pagamento em dinheiro',
    requested: 'Solicitação enviada ao POS',
    posCardReaderTitle: 'Pagamento com terminal',
    posCardReaderBody: 'Esta mesa está configurada para pós-pagamento com terminal de cartão na loja. Peça à equipe para levar o terminal.',
    posCardReaderRequestButton: 'Solicitar terminal de cartão',
    posCardReaderRequested: 'Solicitação enviada ao POS',
  },
  fr: {
    title: 'Paiement en caisse',
    body: 'Cette table est configurée pour un postpaiement en espèces sur place. Demandez au personnel de confirmer le paiement.',
    adjustmentsPolicy: 'Les pourboires se règlent directement avec le personnel et les points ne sont pas disponibles pour ce mode de paiement sur place.',
    requestAmountLabel: 'Montant demandé en caisse (hors pourboire et points)',
    requestButton: 'Demander un paiement en espèces',
    requested: 'Demande envoyée au POS',
    posCardReaderTitle: 'Paiement par terminal',
    posCardReaderBody: 'Cette table est configurée pour un postpaiement par terminal de carte sur place. Demandez au personnel d’apporter le terminal.',
    posCardReaderRequestButton: 'Demander un terminal carte',
    posCardReaderRequested: 'Demande envoyée au POS',
  },
  zh: {
    title: '到店付款',
    body: '此桌设置为店内现金后付款。请让工作人员确认收款。',
    adjustmentsPolicy: '小费请直接与工作人员处理，此店内付款方式不支持使用积分。',
    requestAmountLabel: '店内付款请求金额（不含小费和积分）',
    requestButton: '请求现金付款',
    requested: '请求已发送到 POS',
    posCardReaderTitle: '使用刷卡终端付款',
    posCardReaderBody: '此桌设置为店内刷卡终端后付款。请让工作人员带来刷卡终端。',
    posCardReaderRequestButton: '请求刷卡终端',
    posCardReaderRequested: '请求已发送到 POS',
  },
  ja: {
    title: '店頭でのお支払い',
    body: 'このテーブルは店頭での現金後払いに設定されています。スタッフに支払い確認を依頼してください。',
    adjustmentsPolicy: 'チップはスタッフと直接やり取りしてください。この店頭支払い方法ではポイントは利用できません。',
    requestAmountLabel: '店頭支払い依頼額（チップ・ポイントを除く）',
    requestButton: '現金支払いを依頼',
    requested: 'POSに依頼を送信しました',
    posCardReaderTitle: 'カード端末で支払い',
    posCardReaderBody: 'このテーブルは店頭カード端末での後払いに設定されています。スタッフに端末を依頼してください。',
    posCardReaderRequestButton: 'カード端末を依頼',
    posCardReaderRequested: 'POSに依頼を送信しました',
  },
  hi: {
    title: 'स्टोर पर भुगतान',
    body: 'यह टेबल स्टोर में नकद पोस्टपे के लिए सेट है। भुगतान की पुष्टि के लिए स्टाफ से कहें।',
    adjustmentsPolicy: 'टिप सीधे स्टाफ के साथ दी जाती है, और इस स्टोर भुगतान विधि में अंक उपलब्ध नहीं हैं।',
    requestAmountLabel: 'स्टोर भुगतान अनुरोध राशि (टिप और अंक के बिना)',
    requestButton: 'नकद भुगतान का अनुरोध करें',
    requested: 'अनुरोध POS को भेजा गया',
    posCardReaderTitle: 'कार्ड रीडर से भुगतान',
    posCardReaderBody: 'यह टेबल स्टोर में कार्ड रीडर पोस्टपे के लिए सेट है। स्टाफ से टर्मिनल लाने को कहें।',
    posCardReaderRequestButton: 'कार्ड रीडर का अनुरोध करें',
    posCardReaderRequested: 'अनुरोध POS को भेजा गया',
  },
  de: {
    title: 'Zahlung an der Kasse',
    body: 'Dieser Tisch ist für Barzahlung vor Ort nach dem Essen eingerichtet. Bitte das Personal um Zahlungsbestätigung.',
    adjustmentsPolicy: 'Trinkgeld wird direkt mit dem Personal abgewickelt, und Punkte sind für diese Zahlung vor Ort nicht verfügbar.',
    requestAmountLabel: 'Angeforderter Kassenbetrag (ohne Trinkgeld und Punkte)',
    requestButton: 'Barzahlung anfordern',
    requested: 'Anfrage an POS gesendet',
    posCardReaderTitle: 'Zahlung mit Kartenterminal',
    posCardReaderBody: 'Dieser Tisch ist für Zahlung mit Kartenterminal vor Ort nach dem Essen eingerichtet. Bitte das Personal, das Terminal zu bringen.',
    posCardReaderRequestButton: 'Kartenterminal anfordern',
    posCardReaderRequested: 'Anfrage an POS gesendet',
  },
  it: {
    title: 'Pagamento in cassa',
    body: 'Questo tavolo è configurato per il pagamento in contanti in negozio. Chiedi al personale di confermare il pagamento.',
    adjustmentsPolicy: 'Le mance si gestiscono direttamente con il personale e i punti non sono disponibili per questo metodo di pagamento in negozio.',
    requestAmountLabel: 'Importo richiesto in cassa (senza mancia né punti)',
    requestButton: 'Richiedi pagamento in contanti',
    requested: 'Richiesta inviata al POS',
    posCardReaderTitle: 'Pagamento con terminale',
    posCardReaderBody: 'Questo tavolo è configurato per il pagamento con terminale carta in negozio. Chiedi al personale di portare il terminale.',
    posCardReaderRequestButton: 'Richiedi terminale carta',
    posCardReaderRequested: 'Richiesta inviata al POS',
  },
  ru: {
    title: 'Оплата на кассе',
    body: 'Этот стол настроен на оплату наличными в заведении после заказа. Попросите персонал подтвердить оплату.',
    adjustmentsPolicy: 'Чаевые передаются персоналу напрямую, а баллы недоступны для этого способа оплаты в заведении.',
    requestAmountLabel: 'Сумма запроса на кассе (без чаевых и баллов)',
    requestButton: 'Запросить оплату наличными',
    requested: 'Запрос отправлен в POS',
    posCardReaderTitle: 'Оплата через терминал',
    posCardReaderBody: 'Этот стол настроен на оплату картой через терминал в заведении после заказа. Попросите персонал принести терминал.',
    posCardReaderRequestButton: 'Запросить терминал карты',
    posCardReaderRequested: 'Запрос отправлен в POS',
  },
  ar: {
    title: 'الدفع في المتجر',
    body: 'هذه الطاولة مضبوطة على الدفع النقدي في المتجر بعد الطلب. اطلب من الموظفين تأكيد الدفع.',
    adjustmentsPolicy: 'تُدفع الإكرامية مباشرة للموظفين، ولا تتوفر النقاط لطريقة الدفع هذه في المتجر.',
    requestAmountLabel: 'مبلغ طلب الدفع في المتجر (من دون إكرامية أو نقاط)',
    requestButton: 'طلب الدفع نقدًا',
    requested: 'تم إرسال الطلب إلى نقطة البيع',
    posCardReaderTitle: 'الدفع عبر جهاز البطاقة',
    posCardReaderBody: 'هذه الطاولة مضبوطة على الدفع عبر جهاز البطاقة في المتجر بعد الطلب. اطلب من الموظفين إحضار الجهاز.',
    posCardReaderRequestButton: 'طلب جهاز البطاقة',
    posCardReaderRequested: 'تم إرسال الطلب إلى نقطة البيع',
  },
  vi: {
    title: 'Thanh toán tại quầy',
    body: 'Bàn này được cấu hình trả sau bằng tiền mặt tại cửa hàng. Vui lòng nhờ nhân viên xác nhận thanh toán.',
    adjustmentsPolicy: 'Tiền boa được xử lý trực tiếp với nhân viên và điểm không áp dụng cho phương thức thanh toán tại cửa hàng này.',
    requestAmountLabel: 'Số tiền yêu cầu tại quầy (không gồm tiền boa và điểm)',
    requestButton: 'Yêu cầu thanh toán tiền mặt',
    requested: 'Đã gửi yêu cầu đến POS',
    posCardReaderTitle: 'Thanh toán bằng máy quẹt thẻ',
    posCardReaderBody: 'Bàn này được cấu hình trả sau bằng máy quẹt thẻ tại cửa hàng. Vui lòng nhờ nhân viên mang máy đến.',
    posCardReaderRequestButton: 'Yêu cầu máy quẹt thẻ',
    posCardReaderRequested: 'Đã gửi yêu cầu đến POS',
  },
  th: {
    title: 'ชำระเงินที่ร้าน',
    body: 'โต๊ะนี้ตั้งค่าให้ชำระเงินสดที่ร้านหลังสั่งอาหาร โปรดแจ้งพนักงานให้ยืนยันการชำระเงิน',
    adjustmentsPolicy: 'ทิปจะจัดการกับพนักงานโดยตรง และไม่สามารถใช้คะแนนกับวิธีชำระเงินที่ร้านแบบนี้ได้',
    requestAmountLabel: 'ยอดคำขอชำระที่ร้าน (ไม่รวมทิปและคะแนน)',
    requestButton: 'ขอชำระเงินสด',
    requested: 'ส่งคำขอไปยัง POS แล้ว',
    posCardReaderTitle: 'ชำระด้วยเครื่องอ่านบัตร',
    posCardReaderBody: 'โต๊ะนี้ตั้งค่าให้ชำระด้วยเครื่องอ่านบัตรที่ร้านหลังสั่งอาหาร โปรดแจ้งพนักงานให้นำเครื่องมา',
    posCardReaderRequestButton: 'ขอเครื่องอ่านบัตร',
    posCardReaderRequested: 'ส่งคำขอไปยัง POS แล้ว',
  },
  nl: {
    title: 'Betalen bij de kassa',
    body: 'Deze tafel is ingesteld voor contante betaling in de winkel achteraf. Vraag het personeel om de betaling te bevestigen.',
    adjustmentsPolicy: 'Fooien worden rechtstreeks met het personeel afgehandeld en punten zijn niet beschikbaar voor deze betaalmethode in de winkel.',
    requestAmountLabel: 'Aangevraagd kassabedrag (zonder fooi en punten)',
    requestButton: 'Contante betaling aanvragen',
    requested: 'Aanvraag naar POS verzonden',
    posCardReaderTitle: 'Betalen met kaartterminal',
    posCardReaderBody: 'Deze tafel is ingesteld voor betaling met kaartterminal in de winkel achteraf. Vraag het personeel om de terminal te brengen.',
    posCardReaderRequestButton: 'Kaartterminal aanvragen',
    posCardReaderRequested: 'Aanvraag naar POS verzonden',
  },
};

/**
 * 세션 결제 라벨 조립 (페이지 헤더 + 패널 공용).
 *
 * @param locale - 현재 로케일 (15언어)
 * @param count - 미결제 후불 건수 ({count} 치환 — append 모델에서 항상 1)
 */
export function buildSessionChargeLabels(
  locale: Locale,
  count: number,
): SessionChargePanelProps['labels'] {
  const payNowT = payNowTranslations[locale];
  // #522 — Bloomin 카드 폼 라벨 15언어 전수 정의 → narrowing 없이 locale 직접 인덱싱.
  const cardInput = bloominPaymentTranslations[locale].cardInput;

  return {
    title: payNowT.session.modalTitle,
    description: payNowT.session.modalDescription.replace('{count}', String(count)),
    paymentMethodResolutionError: paymentMethodResolutionErrorTranslations[locale],
    cardInput,
    adjustments: payNowT.adjustments,
    manualPostpayNotice: manualPostpayNoticeTranslations[locale],
    errors: {
      notSettleable: payNowT.errors.notSettleable,
      processingCannotCancel: payNowT.errors.processingCannotCancel,
      untrustedChallenge: payNowT.errors.untrustedChallenge,
      adjustmentValidationFailed: payNowT.errors.adjustmentValidationFailed,
      cardRequiredForCharge: payNowT.errors.cardRequiredForCharge,
      generic: payNowT.errors.generic,
    },
  };
}
